export type CartStatus = 'active' | 'ordered' | 'expired'

export interface CartItem {
  competitionId: string
  competitionName: string
  feeAmount: number
  currency: string
  quantity: number
  slotReservedUntil: string
  /** Set when the competition stopped taking registrations after the item was added. */
  unavailableReason?: 'registration_closed' | 'cancelled' | null
}

export const useCartStore = defineStore('cart', () => {
  const items = ref<CartItem[]>([])
  const status = ref<CartStatus>('active')
  const isLoading = ref(false)
  const error = ref<string | null>(null)

  const totalFee = computed(() => items.value.reduce((sum, item) => sum + item.feeAmount * (item.quantity ?? 1), 0))
  const currency = computed(() => items.value[0]?.currency ?? 'THB')
  const count = computed(() => items.value.length)
  const totalSlots = computed(() => items.value.reduce((sum, item) => sum + (item.quantity ?? 1), 0))
  const isActive = computed(() => status.value === 'active')
  const isFrozen = computed(() => status.value === 'ordered')

  async function fetchCart() {
    const { $api } = useNuxtApp()
    isLoading.value = true
    error.value = null
    try {
      const res = await $api<{ cart: { items: CartItem[], status: string } }>('/cart')
      items.value = res.cart.items ?? []
      status.value = (res.cart.status as CartStatus) ?? 'active'
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Failed to load cart'
    } finally {
      isLoading.value = false
    }
  }

  async function addItem(competitionId: string, competitionName: string, feeAmount: number, currencyCode: string) {
    const { $api } = useNuxtApp()
    try {
      const res = await $api<{ cart: { items: CartItem[], status: string } }>('/cart/items', {
        method: 'POST',
        body: { competitionId, competitionName, feeAmount, currency: currencyCode }
      })
      items.value = res.cart.items ?? []
      status.value = (res.cart.status as CartStatus) ?? 'active'
      return true
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Failed to add item'
      return false
    }
  }

  async function removeItem(competitionId: string) {
    const { $api } = useNuxtApp()
    try {
      const res = await $api<{ cart: { items: CartItem[], status: string } }>(`/cart/items/${competitionId}`, {
        method: 'DELETE'
      })
      items.value = res.cart.items ?? []
      status.value = (res.cart.status as CartStatus) ?? 'active'
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Failed to remove item'
    }
  }

  async function updateQuantity(competitionId: string, quantity: number) {
    const { $api } = useNuxtApp()
    try {
      const res = await $api<{ cart: { items: CartItem[], status: string } }>(`/cart/items/${competitionId}`, {
        method: 'PUT',
        body: { quantity }
      })
      items.value = res.cart.items ?? []
      status.value = (res.cart.status as CartStatus) ?? 'active'
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Failed to update quantity'
    }
  }

  async function clear() {
    const { $api } = useNuxtApp()
    try {
      await $api('/cart', { method: 'DELETE' })
      items.value = []
      status.value = 'active'
    } catch (e: unknown) {
      error.value = e instanceof Error ? e.message : 'Failed to clear cart'
    }
  }

  function isInCart(competitionId: string) {
    return items.value.some(i => i.competitionId === competitionId)
  }

  function getQuantity(competitionId: string) {
    return items.value.find(i => i.competitionId === competitionId)?.quantity ?? 0
  }

  return {
    items,
    status,
    isLoading,
    error,
    totalFee,
    currency,
    count,
    totalSlots,
    isActive,
    isFrozen,
    fetchCart,
    addItem,
    updateQuantity,
    removeItem,
    clear,
    isInCart,
    getQuantity
  }
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useCartStore, import.meta.hot))
}
